import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  ShoppingCart,
  Package,
  DollarSign,
  User,
  Calendar,
  AlertCircle,
} from "lucide-react";
import { getCartByIdThunk } from "../../../store/thunks/adminCartThunks";
import CartItemsSection from "./CartDetailsSections";
import LoadingSpinner from "../../../components/common/Loader";

const CartDetails = () => {
  const { cartId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedCart, loading, error } = useSelector((state) => state.cart);

  useEffect(() => {
    dispatch(getCartByIdThunk(cartId));
  }, [dispatch, cartId]);

  if (loading) return <LoadingSpinner />;
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          Error loading cart: {error}
        </div>
      </div>
    );
  }

  if (!selectedCart) return null;

  const {
    user,
    items,
    exchangeItems,
    summary,
    status,
    createdAt,
    lastActivity,
  } = selectedCart;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cart Details</h1>
            <p className="text-gray-600">Cart ID: {cartId}</p>
          </div>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-sm font-medium ${
            status === "ACTIVE"
              ? "bg-green-100 text-green-800"
              : status === "ABANDONED"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-blue-100 text-blue-800"
          }`}
        >
          {status}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* User Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Customer Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{user?.name || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{user?.email || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium">{user?.phone || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Customer Type</p>
                <p className="font-medium">{user?.customerType || "RETAIL"}</p>
              </div>
            </div>
          </div>

          {/* Cart Items */}
          <CartItemsSection items={items} exchangeItems={exchangeItems} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Price Summary
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Metal Value</span>
                <span className="font-medium">
                  ₹{summary?.totalMetalValue?.toLocaleString() || 0}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Making Charges</span>
                <span className="font-medium">
                  ₹{summary?.totalMakingCharges?.toLocaleString() || 0}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Stone Charges</span>
                <span className="font-medium">
                  ₹{summary?.totalStoneCharges?.toLocaleString() || 0}
                </span>
              </div>
              {summary?.totalDiscount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount</span>
                  <span className="font-medium text-green-600">
                    -₹{summary.totalDiscount.toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">GST</span>
                <span className="font-medium">
                  ₹{summary?.totalGST?.toLocaleString() || 0}
                </span>
              </div>

              <div className="border-t pt-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">
                    ₹{summary?.totalAmount?.toLocaleString() || 0}
                  </span>
                </div>
              </div>

              {summary?.netExchangeValue > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Exchange Value</span>
                    <span className="font-medium text-green-600">
                      -₹{summary.netExchangeValue.toLocaleString()}
                    </span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold">Net Payable</span>
                      <span className="text-lg font-bold">
                        ₹{summary?.netPayable?.toLocaleString() || 0}
                      </span>
                    </div>
                  </div>
                </>
              )}

              {!summary?.netExchangeValue && (
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="font-semibold">Total Amount</span>
                    <span className="text-lg font-bold">
                      ₹{summary?.totalAmount?.toLocaleString() || 0}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Timeline
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Created</p>
                <p className="font-medium">
                  {new Date(createdAt).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Last Activity</p>
                <p className="font-medium">
                  {new Date(lastActivity).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Stock Status */}
          {!summary?.allItemsAvailable && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">Stock Issues</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    {summary?.unavailableItems?.length || 0} items have
                    insufficient stock
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartDetails;
